import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { announcementsApi } from '../../services/api';
import { useT } from '../../i18n';
import { formatDate as _formatDateBaku } from '../../utils/date';

const CAT_CFG = {
  general:     { labelKey: 'ann_general',     icon: '📢', color: '#3b82f6' },
  urgent:      { labelKey: 'ann_urgent',      icon: '🚨', color: '#ef4444' },
  maintenance: { labelKey: 'ann_maintenance', icon: '🔧', color: '#f59e0b' },
  event:       { labelKey: 'ann_event',       icon: '🎉', color: '#10b981' },
};

const EMPTY_FORM = {
  title: '', body: '', category: 'general',
  isPinned: false, isActive: true,
  publishAt: '', expiresAt: '', imageUrl: '',
  targetRoles: 'all',
};

function fmtDate(dt) {
  return _formatDateBaku(dt, 'datetime');
}

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
        background: checked ? 'var(--success)' : 'var(--border)',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

export default function Announcements() {
  const t = useT();
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const [newMode,    setNewMode]    = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [catFilter,  setCatFilter]  = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await announcementsApi.list();
      setItems(Array.isArray(d) ? d : []);
    } catch {
      toast.error('Elanlar yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setNewMode(true);
  };

  const openEdit = (item) => {
    setNewMode(false);
    setSelected(item);
    setForm({
      title:       item.title       || '',
      body:        item.body        || '',
      category:    item.category    || 'general',
      isPinned:    !!item.isPinned,
      isActive:    item.isActive !== false,
      publishAt:   item.publishAt   ? item.publishAt.slice(0, 16) : '',
      expiresAt:   item.expiresAt   ? item.expiresAt.slice(0, 16) : '',
      imageUrl:    item.imageUrl    || '',
      targetRoles: Array.isArray(item.targetRoles) ? (item.targetRoles[0] || 'all') : (item.targetRoles || 'all'),
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Başlıq boş ola bilməz'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        title:     form.title.trim(),
        body:        form.body.trim()     || '',
        imageUrl:    form.imageUrl.trim() || undefined,
        publishAt:   form.publishAt       || undefined,
        expiresAt:   form.expiresAt       || undefined,
        targetRoles: [form.targetRoles],
      };
      if (selected && !newMode) {
        const u = await announcementsApi.update(selected.id, payload);
        setItems(p => p.map(x => x.id === u.id ? u : x));
        setSelected(u);
        toast.success(t('save') + ' ✓');
      } else {
        const c = await announcementsApi.create(payload);
        setItems(p => [c, ...p]);
        setSelected(c);
        setNewMode(false);
        toast.success(t('ann_title') + ' ' + t('create') + ' ✓');
      }
    } catch (err) {
      toast.error(err?.message || 'Xəta baş verdi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm(t('confirm_delete'))) return;
    try {
      await announcementsApi.remove(selected.id);
      setItems(p => p.filter(x => x.id !== selected.id));
      setSelected(null);
      setNewMode(false);
      toast.success(t('delete') + ' ✓');
    } catch (err) {
      toast.error(err?.message || 'Silmə xətası');
    }
  };

  const filtered = catFilter === 'all'
    ? items
    : items.filter(x => x.category === catFilter);

  const showForm = newMode || !!selected;

  return (
    <div style={S.root}>

      {/* ══ LEFT PANEL ══ */}
      <div style={S.left}>
        <div style={S.leftHead}>
          <div>
            <h1 style={S.title}>{t('ann_title')}</h1>
            <p style={S.sub}>{items.length} elan</p>
          </div>
          <button style={S.addBtn} onClick={openNew}>+ {t('ann_new')}</button>
        </div>

        {/* Category filter tabs */}
        <div style={S.filterRow}>
          {[
            ['all', t('ann_all')],
            ['general', t('ann_general')],
            ['urgent', t('ann_urgent')],
            ['maintenance', t('ann_maintenance')],
            ['event', t('ann_event')],
          ].map(([k, l]) => (
            <button key={k}
              style={{
                ...S.filterBtn,
                color: catFilter === k ? (CAT_CFG[k]?.color || 'var(--primary)') : 'var(--text3)',
                borderColor: catFilter === k ? (CAT_CFG[k]?.color || 'var(--primary)') + '60' : 'transparent',
                background: catFilter === k ? (CAT_CFG[k]?.color || 'var(--primary)') + '15' : 'transparent',
              }}
              onClick={() => setCatFilter(k)}>
              {l}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div style={S.hint}>{t('loading')}</div>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>{t('ann_title')} yoxdur</div>
        ) : filtered.map(item => {
          const cfgRaw = CAT_CFG[item.category] || CAT_CFG.general;
          const cfg = { ...cfgRaw, label: cfgRaw.icon + ' ' + t(cfgRaw.labelKey) };
          const isSel = selected?.id === item.id && !newMode;
          return (
            <div key={item.id}
              style={{ ...S.item, ...(isSel ? S.itemSel : {}) }}
              onClick={() => openEdit(item)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.itemTitle}>
                  {item.isPinned && <span style={{ marginRight: 4 }}>📌</span>}
                  {item.title}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ ...S.badge, color: cfg.color, background: cfg.color + '18', borderColor: cfg.color + '35' }}>
                    {cfg.label}
                  </span>
                  <span style={S.itemDate}>{fmtDate(item.publishAt || item.createdAt)}</span>
                </div>
              </div>
              <div style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: item.isActive ? 'var(--success)' : 'var(--text3)',
              }} />
            </div>
          );
        })}
      </div>

      {/* ══ RIGHT PANEL ══ */}
      <div style={S.right}>
        {showForm ? (
          <form onSubmit={handleSubmit} style={S.formWrap}>
            <div style={S.formHead}>
              <div style={S.formTitle}>
                {newMode ? '+ ' + t('ann_new') : t('edit') + ' — ' + t('ann_title')}
              </div>
              <button type="button" style={S.closeBtn} onClick={() => { setSelected(null); setNewMode(false); }}>✕</button>
            </div>

            <div style={S.formBody}>
              {/* Title */}
              <div style={S.lbl}>Başlıq *</div>
              <input style={S.inp} placeholder="Elan başlığı..."
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                required />

              {/* Body */}
              <div style={S.lbl}>Mətn</div>
              <textarea style={{ ...S.inp, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Elan mətni..."
                rows={5}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />

              {/* Category */}
              <div style={S.lbl}>Kateqoriya</div>
              <select style={S.inp} value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="general">📢 {t('ann_general')}</option>
                <option value="urgent">🚨 {t('ann_urgent')}</option>
                <option value="maintenance">🔧 {t('ann_maintenance')}</option>
                <option value="event">🎉 {t('ann_event')}</option>
              </select>

              {/* Toggles row */}
              <div style={S.toggleRow}>
                <div style={S.toggleItem}>
                  <div>
                    <div style={S.lbl}>📌 Sabitleşdir</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Yuxarıda göstər</div>
                  </div>
                  <Toggle checked={form.isPinned} onChange={v => setForm(f => ({ ...f, isPinned: v }))} />
                </div>
                <div style={S.toggleItem}>
                  <div>
                    <div style={S.lbl}>{t('active')}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>İstifadəçilərə göstər</div>
                  </div>
                  <Toggle checked={form.isActive} onChange={v => setForm(f => ({ ...f, isActive: v }))} />
                </div>
              </div>

              {/* Date range */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <div style={S.lbl}>Yayım tarixi (opsional)</div>
                  <input style={S.inp} type="datetime-local"
                    value={form.publishAt}
                    onChange={e => setForm(f => ({ ...f, publishAt: e.target.value }))} />
                </div>
                <div>
                  <div style={S.lbl}>Bitmə tarixi (opsional)</div>
                  <input style={S.inp} type="datetime-local"
                    value={form.expiresAt}
                    onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
                </div>
              </div>

              {/* Image URL */}
              <div style={S.lbl}>Şəkil URL (opsional)</div>
              <input style={S.inp} placeholder="https://..."
                value={form.imageUrl}
                onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} />

              {/* Target roles */}
              <div style={S.lbl}>Hədəf auditoriya</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[['all', t('all')], ['clients', t('users_clients')], ['staff', t('users_staff')]].map(([v, l]) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text2)' }}>
                    <input type="radio" name="targetRoles" value={v}
                      checked={form.targetRoles === v}
                      onChange={() => setForm(f => ({ ...f, targetRoles: v }))}
                      style={{ accentColor: 'var(--primary)' }} />
                    {l}
                  </label>
                ))}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit" style={{ ...S.btn, flex: 1 }} disabled={saving}>
                  {saving ? t('saving') : selected && !newMode ? '✓ ' + t('save') : '+ ' + t('create')}
                </button>
                {selected && !newMode && (
                  <button type="button" style={S.btnDel} onClick={handleDelete}>
                    {t('delete')}
                  </button>
                )}
              </div>
            </div>
          </form>
        ) : (
          <div style={S.emptyState}>
            <div style={S.emptyIcon}>📢</div>
            <div style={S.emptyMsg}>{t('view')} / {t('ann_title')}</div>
            <div style={S.emptySub}>və ya "+ {t('ann_new')}" düyməsinə basın</div>
          </div>
        )}
      </div>

    </div>
  );
}

const S = {
  root:  { display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, height: 'calc(100vh - 64px)' },
  left:  { display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', paddingRight: 2 },
  right: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column' },

  leftHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 4 },
  title:     { fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 2 },
  sub:       { color: 'var(--text3)', fontSize: 12 },
  addBtn:    { background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },

  filterRow: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 },
  filterBtn: { padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', transition: 'all 0.15s' },

  item:    { display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' },
  itemSel: { borderColor: 'var(--primary)', background: 'var(--primary)08' },
  itemTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  itemDate:  { fontSize: 10, color: 'var(--text3)' },
  badge:   { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, border: '1px solid transparent', whiteSpace: 'nowrap' },

  hint:  { textAlign: 'center', color: 'var(--text3)', padding: 24, fontSize: 12 },
  empty: { textAlign: 'center', color: 'var(--text3)', padding: 24, background: 'var(--bg2)', borderRadius: 10, border: '1px dashed var(--border)', fontSize: 12 },

  formWrap: { display: 'flex', flexDirection: 'column', height: '100%' },
  formHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  formTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  closeBtn:  { background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 17, padding: '4px 6px' },
  formBody:  { flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 10 },

  lbl: { fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 },
  inp: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 11px', color: 'var(--text)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s' },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  btnDel: { background: 'var(--danger)15', color: 'var(--danger)', border: '1px solid var(--danger)25', borderRadius: 7, padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 },

  toggleRow:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  toggleItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' },

  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 },
  emptyIcon:  { fontSize: 44, opacity: 0.15 },
  emptyMsg:   { color: 'var(--text3)', fontSize: 14, fontWeight: 600 },
  emptySub:   { color: 'var(--text3)', fontSize: 11, opacity: 0.6 },
};
